import { axios } from "../axiosInstance";

export interface ManageDealActor {
  contactId: string;
  companyId: string;
  isFirmAdmin: boolean;
  firstName: string;
  lastName: string;
}

export interface ManageDealRow {
  dealId: number;
  title: string;
  dealType: string;
  dealAmount: string;
  valueRank: number;
  postedDate: string;
  postedSort: number;
  approved: string;
}

export interface ManageDealListResult {
  active: ManageDealRow[];
  archived: ManageDealRow[];
}

export interface ManageDealOption {
  id: number;
  name: string;
}

export interface ManageDealDraft {
  dealId: number;
  dealContactId: number;
  openEnded: boolean;
  dateFrom: string;
  dateTo: string;
  title: string;
  dealTypeId: number;
  dealAmount: string;
  governingLaw: string;
  countryIds: number[];
  regionIds: number[];
  industryIds: number[];
  allIndustries: boolean;
  description: string;
  additionalInfo: string;
  notifyRegions: number[];
}

export interface ManageDealEditor {
  contacts: ManageDealOption[];
  dealTypes: ManageDealOption[];
  industries: ManageDealOption[];
  regions: ManageDealOption[];
  countries: ManageDealOption[];
  deal: ManageDealDraft | null;
}

export interface ManageDealSavePayload {
  mode: "add" | "update";
  dealId: number | null;
  dealContactId: number;
  openEnded: boolean;
  dateFrom: string;
  dateTo: string;
  title: string;
  dealTypeId: number;
  customDealType: string;
  dealAmount: string;
  governingLaw: string;
  countryIds: number[];
  industryIds: number[];
  allIndustries: boolean;
  description: string;
  additionalInfo: string;
  notifyRegions: number[];
}

export interface ManageDealSaveResult {
  saved: boolean;
  errors: string[];
  message: string;
  dealId: number | null;
}

function queryString(params: Record<string, string>): string {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      search.set(key, value);
    }
  });
  const text = search.toString();
  return text ? `?${text}` : "";
}

function actorQuery(actor: ManageDealActor): Record<string, string> {
  return {
    contactId: actor.contactId,
    companyId: actor.companyId,
    firmAdmin: actor.isFirmAdmin ? "1" : "0",
    firstName: actor.firstName,
    lastName: actor.lastName,
  };
}

export async function getManageDealList(actor: ManageDealActor): Promise<ManageDealListResult> {
  const response = await axios.Get(`./manageDealmaker${queryString({ view: "list", ...actorQuery(actor) })}`);
  return response.data as ManageDealListResult;
}

export async function getManageDealEditor(actor: ManageDealActor, dealId: string, regions: string): Promise<ManageDealEditor> {
  const response = await axios.Get(`./manageDealmaker${queryString({ view: "editor", ...actorQuery(actor), dealId, regions })}`);
  return response.data as ManageDealEditor;
}

export async function getManageDealCountries(actor: ManageDealActor, regions: string): Promise<ManageDealOption[]> {
  const response = await axios.Get(`./manageDealmaker${queryString({ view: "countries", ...actorQuery(actor), regions })}`);
  return response.data as ManageDealOption[];
}

export async function saveManageDeal(actor: ManageDealActor, payload: ManageDealSavePayload): Promise<ManageDealSaveResult> {
  const response = await axios.Post("./manageDealmaker", { action: "save", ...actor, ...payload });
  return response.data as ManageDealSaveResult;
}
